import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Ruler, Sparkles, MapPin, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { getFieldQuoteMeasurements, ProjectMeasurement } from '../lib/projectMeasurements';
import { getMeasurementPhotos, getMeasurementPhotoUrl } from '../services/measurementPhotoService';
import TokenPurchaseModal from './TokenPurchaseModal';
import { toast } from './toasts/SimpleToast';

/** An order item block produced by AI from a field-quote measurement. */
export interface MeasurementOrderItem {
  measurementId: string;
  name: string;
  comments: string;
  quantity: number;
  /** Public URLs of the measurement's photos, attached as reference images. */
  images: string[];
}

interface FieldQuoteRow {
  id: string;
  status: string | null;
  is_measurement: boolean | null;
  start_date: string | null;
  field_quote_address: string | null;
  created_at: string;
  customer: { first_name: string | null; last_name: string | null } | null;
  project: { title: string | null } | null;
}

interface SelectFieldQuoteMeasurementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierName?: string;
  /** Called with the AI-generated item blocks (photos already resolved) and the
   * source field quote id, so the resulting PO can link to the customer journey. */
  onItemsCreated: (items: MeasurementOrderItem[], fieldQuoteId: string) => void;
}

const measurementSummary = (m: ProjectMeasurement): string => {
  const parts: string[] = [];
  if (m.length) parts.push(`L: ${m.length}${m.unit || ''}`);
  if (m.width) parts.push(`W: ${m.width}${m.unit || ''}`);
  if (m.height) parts.push(`H: ${m.height}${m.unit || ''}`);
  if (m.area) parts.push(`Area: ${m.area}${m.unit || ''}²`);
  return parts.join(' · ');
};

export default function SelectFieldQuoteMeasurementsModal({
  isOpen,
  onClose,
  supplierName,
  onItemsCreated,
}: SelectFieldQuoteMeasurementsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [step, setStep] = useState<'quotes' | 'measurements'>('quotes');
  const [quotes, setQuotes] = useState<FieldQuoteRow[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<FieldQuoteRow | null>(null);
  const [measurements, setMeasurements] = useState<ProjectMeasurement[]>([]);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);

  const isMobile = window.innerWidth < 768;

  const loadQuotes = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      setQuotesLoading(true);
      // !inner join on measurements → only quotes that have at least one
      // measurement added. Newest start date first, undated quotes last.
      const { data, error } = await supabase
        .from('project_field_quotes')
        .select('id, status, is_measurement, start_date, field_quote_address, created_at, customer:customers(first_name, last_name), project:projects(title), project_measurements!inner(id)')
        .eq('business_id', selectedBusiness.id)
        .order('start_date', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      setQuotes((data as unknown as FieldQuoteRow[]) || []);
    } catch (err) {
      console.error('Error loading field quotes:', err);
      toast.error({ title: 'Error', description: 'Failed to load field quotes', duration: 4000 });
    } finally {
      setQuotesLoading(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setStep('quotes');
      setSelectedQuote(null);
      setMeasurements([]);
      setSelectedIds(new Set());
      loadQuotes();
    }
  }, [isOpen, loadQuotes]);

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  const openQuote = async (quote: FieldQuoteRow) => {
    setSelectedQuote(quote);
    setStep('measurements');
    setSelectedIds(new Set());
    try {
      setMeasurementsLoading(true);
      const data = await getFieldQuoteMeasurements(quote.id);
      setMeasurements(data);
    } catch (err) {
      console.error('Error loading measurements:', err);
      toast.error({ title: 'Error', description: 'Failed to load measurements', duration: 4000 });
      setMeasurements([]);
    } finally {
      setMeasurementsLoading(false);
    }
  };

  const toggleMeasurement = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!selectedBusiness?.id || !supabase || selectedIds.size === 0) return;
    const selected = measurements.filter(m => selectedIds.has(m.id));

    try {
      setGenerating(true);

      const { data, error } = await supabase.functions.invoke('ai-create-order-items', {
        body: {
          businessId: selectedBusiness.id,
          supplierName: supplierName || undefined,
          measurements: selected.map(m => ({
            id: m.id,
            name: m.name,
            category: m.category ?? null,
            length: m.length ?? null,
            width: m.width ?? null,
            height: m.height ?? null,
            area: m.area ?? null,
            unit: m.unit,
            custom_fields: m.custom_fields ?? null,
            notes: m.notes ?? null,
          })),
        },
      });

      if (error) throw error;
      if (data?.insufficientTokens) {
        setShowTokenPurchaseModal(true);
        return;
      }
      if (!data?.success || !Array.isArray(data.items)) {
        throw new Error(data?.error || 'AI could not create items from these measurements');
      }

      // Attach every measurement photo to its generated item as a reference image.
      const photosById = new Map<string, string[]>();
      await Promise.all(selected.map(async (m) => {
        try {
          const photos = await getMeasurementPhotos(m.id);
          photosById.set(m.id, photos.map(p => getMeasurementPhotoUrl(p.file_path)));
        } catch (err) {
          console.error('Error loading measurement photos:', err);
          photosById.set(m.id, []);
        }
      }));

      const items: MeasurementOrderItem[] = data.items.map((it: { measurementId: string; name: string; comments: string; quantity: number }) => ({
        measurementId: it.measurementId,
        name: it.name,
        comments: it.comments,
        quantity: it.quantity,
        images: photosById.get(it.measurementId) || [],
      }));

      onItemsCreated(items, selectedQuote!.id);
      onClose();
    } catch (err) {
      console.error('Error creating items from measurements:', err);
      toast.error({
        title: 'AI Error',
        description: err instanceof Error ? err.message : 'Failed to create items from measurements',
        duration: 5000,
      });
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const quoteTitle = (q: FieldQuoteRow): string => {
    const customerName = `${q.customer?.first_name || ''} ${q.customer?.last_name || ''}`.trim();
    if (customerName) return customerName;
    if (q.project?.title) return q.project.title;
    return q.is_measurement ? 'Field Measurements' : 'Field Quote';
  };

  const modalContent = (
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${isOpen ? 'bg-black/50' : 'bg-black/0'}`}
      style={{
        zIndex: 100090, // Above OneTimeItemOrderModal (100070)
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100100,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            {step === 'measurements' && (
              <button
                onClick={() => setStep('quotes')}
                className="absolute top-1/2 -translate-y-1/2 left-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                aria-label="Back to field quotes"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              {step === 'quotes' ? 'Select Field Quote' : 'Select Measurements'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {step === 'quotes'
                ? 'Choose the field quote with the measurements you want to order'
                : selectedQuote ? quoteTitle(selectedQuote) : ''}
            </p>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-3`}>
              {step === 'quotes' && (
                quotesLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : quotes.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-500">
                    No field quotes found for this business.
                  </div>
                ) : (
                  quotes.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => openQuote(q)}
                      className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 active:scale-[0.99] transition-all duration-150 text-left touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Ruler className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">{quoteTitle(q)}</p>
                          {q.is_measurement && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold text-blue-600 bg-blue-100 rounded-full flex-shrink-0">
                              Measurements
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                          {q.start_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {q.start_date}
                            </span>
                          )}
                          {q.field_quote_address && (
                            <span className="flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{q.field_quote_address}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>
                  ))
                )
              )}

              {step === 'measurements' && (
                measurementsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : measurements.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-500">
                    This field quote has no measurements yet.
                  </div>
                ) : (
                  measurements.map((m) => {
                    const checked = selectedIds.has(m.id);
                    const summary = measurementSummary(m);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleMeasurement(m.id)}
                        className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all duration-150 text-left touch-manipulation active:scale-[0.99] ${
                          checked
                            ? 'bg-blue-50 border-blue-400'
                            : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div
                          className={`h-5 w-5 rounded-md border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                            checked ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'
                          }`}
                        >
                          {checked && (
                            <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-gray-900 truncate">{m.name}</p>
                            {m.category && (
                              <span className="px-2 py-0.5 text-[10px] font-semibold text-gray-600 bg-gray-200 rounded-full flex-shrink-0">
                                {m.category}
                              </span>
                            )}
                          </div>
                          {summary && <p className="text-xs text-gray-600 mt-0.5">{summary}</p>}
                          {m.notes?.trim() && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">{m.notes.trim()}</p>
                          )}
                        </div>
                      </button>
                    );
                  })
                )
              )}
            </div>
          </div>

          {/* Footer */}
          {step === 'measurements' && (
            <div className="border-t border-gray-200 p-6 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating || selectedIds.size === 0}
                className="w-full p-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 shadow-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Creating Items...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {selectedIds.size > 0
                      ? `Create ${selectedIds.size} Item${selectedIds.size === 1 ? '' : 's'} with AI`
                      : 'Select measurements to continue'}
                  </>
                )}
              </button>
              <p className="text-[11px] text-gray-500 text-center mt-2">
                Uses AI tokens. Measurement photos are attached to each item automatically.
              </p>
            </div>
          )}
        </div>
      </div>

      {showTokenPurchaseModal && (
        <TokenPurchaseModal
          isOpen={showTokenPurchaseModal}
          onClose={() => setShowTokenPurchaseModal(false)}
          businessId={selectedBusiness?.id || ''}
          onSuccess={() => setShowTokenPurchaseModal(false)}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
